import RightTimeApi from "@/API";
import Enums from "@/enums";

const WEB_VERSION_KEY = "RightTime.KnownWebVersion";
const API_VERSION_KEY = "RightTime.KnownApiVersion";

export type VersionAuditOptions = {
  webVersion?: string | null;
  apiVersion?: string | null;
  companyId?: number | null;
  userId?: number | null;
};

function readStoredVersion(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredVersion(key: string, version: string): void {
  try {
    localStorage.setItem(key, version);
  } catch {
    // ignore storage failures (private browsing, quota, etc.)
  }
}

async function logVersionChangeAsync(
  message: string,
  options: VersionAuditOptions,
): Promise<void> {
  try {
    await RightTimeApi.putAsync("/log", {
      companyId: options.companyId ?? null,
      context: "Version change",
      userAgent: navigator.userAgent,
      language: navigator.language,
      message,
      userId: options.userId ?? null,
      actionId: Enums.ENTITY_ACTION.Logging,
      entityTypeId: Enums.ENTITY_TYPE.Account,
    });
  } catch (error) {
    console.error("Failed to log version change:", error);
  }
}

async function auditSingleVersionChangeAsync(
  key: string,
  label: "Frontend" | "Backend",
  nextVersion: string,
  options: VersionAuditOptions,
): Promise<void> {
  const trimmed = nextVersion.trim();
  if (!trimmed) return;

  const previous = readStoredVersion(key);
  if (previous && previous !== trimmed) {
    await logVersionChangeAsync(
      `${label} version changed from ${previous} to ${trimmed}`,
      options,
    );
  }

  writeStoredVersion(key, trimmed);
}

/** Writes audit log entries when the loaded web or API version differs from the last known value. */
export async function auditVersionChangesAsync(options: VersionAuditOptions): Promise<void> {
  const tasks: Promise<void>[] = [];

  if (options.webVersion) {
    tasks.push(
      auditSingleVersionChangeAsync(
        WEB_VERSION_KEY,
        "Frontend",
        options.webVersion,
        options,
      ),
    );
  }

  if (options.apiVersion) {
    tasks.push(
      auditSingleVersionChangeAsync(
        API_VERSION_KEY,
        "Backend",
        options.apiVersion,
        options,
      ),
    );
  }

  await Promise.all(tasks);
}
